/**
 * 系统状态页面 - 双系统状态查看和切换
 * 零侵入：独立页面，不影响现有系统
 * 路由：/system-status
 */

import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  Description as JsonIcon,
  AccountCircle as UserIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useAuthStore, authUtils } from '../stores/authStore';
import { Link as RouterLink } from 'react-router-dom';

const SystemStatus = () => {
  const {
    authSystem,
    mongoAvailable,
    mongoUser,
    switchAuthSystem,
    logout,
    checkMongoHealth,
    isLoggedInMongo,
    isLoggedInJson,
  } = useAuthStore();

  const [checking, setChecking] = React.useState(false);

  const handleCheckHealth = async () => {
    setChecking(true);
    await checkMongoHealth();
    setChecking(false);
  };

  const currentUser = authUtils.getCurrentUser();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        系统状态
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        短剧平台支持双系统并行运行，您可以随时切换。
      </Typography>

      <Grid container spacing={3}>
        {/* 当前系统状态 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              当前认证系统
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip
                label={authSystem === 'mongodb' ? 'MongoDB 新系统' : 'JSON 旧系统'}
                color={authSystem === 'mongodb' ? 'primary' : 'default'}
                icon={authSystem === 'mongodb' ? <StarIcon /> : <JsonIcon />}
              />
              {isLoggedInMongo && (
                <Chip
                  label={`已登录: ${mongoUser?.username || currentUser?.username}`}
                  color="success"
                  icon={<UserIcon />}
                />
              )}
              {isLoggedInJson && !isLoggedInMongo && (
                <Chip
                  label={`已登录: ${currentUser?.username}`}
                  color="success"
                  variant="outlined"
                  icon={<UserIcon />}
                />
              )}
            </Box>
            {(isLoggedInMongo || isLoggedInJson) && (
              <Button
                variant="outlined"
                color="error"
                onClick={logout}
                component={RouterLink}
                to="/"
              >
                退出登录
              </Button>
            )}
          </Paper>
        </Grid>

        {/* MongoDB 系统卡片 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  MongoDB 新系统
                </Typography>
                <Chip
                  label={mongoAvailable ? '运行正常' : '连接异常'}
                  color={mongoAvailable ? 'success' : 'error'}
                  size="small"
                  icon={mongoAvailable ? <CheckCircleIcon /> : <ErrorIcon />}
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" paragraph>
                采用 MongoDB 存储，支持完整的用户生态系统：
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2">SHA256 + 盐值密码加密</Typography>
                <Typography component="li" variant="body2">6 级会员等级体系</Typography>
                <Typography component="li" variant="body2">7 级用户成长积分</Typography>
                <Typography component="li" variant="body2">充值订单管理</Typography>
                <Typography component="li" variant="body2">完整的操作日志记录</Typography>
              </Box>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/login-v2"
                disabled={!mongoAvailable}
              >
                登录新系统
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/register-v2"
                disabled={!mongoAvailable}
              >
                注册账户
              </Button>
              <Button
                onClick={handleCheckHealth}
                disabled={checking}
                size="small"
              >
                {checking ? '检查中...' : '刷新状态'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* JSON 系统卡片 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <JsonIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  JSON 旧系统
                </Typography>
                <Chip
                  label="稳定运行"
                  color="success"
                  variant="outlined"
                  size="small"
                  icon={<CheckCircleIcon />}
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" paragraph>
                采用本地 JSON 文件存储，稳定可靠：
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2">简单的用户认证系统</Typography>
                <Typography component="li" variant="body2">剧集和评论管理</Typography>
                <Typography component="li" variant="body2">广告配置系统</Typography>
                <Typography component="li" variant="body2">观看历史和收藏</Typography>
                <Typography component="li" variant="body2">
                  <Alert severity="warning" sx={{ mt: 1, p: 1 }} icon={<ErrorIcon />}>
                    <Typography variant="caption">
                      ⚠️ 密码以明文存储，建议仅用于测试
                    </Typography>
                  </Alert>
                </Typography>
              </Box>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button variant="contained" component={RouterLink} to="/login">
                登录旧系统
              </Button>
              <Button variant="outlined" component={RouterLink} to="/register">
                注册账户
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* 系统切换说明 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              🔄 系统切换指南
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    从 JSON 切换到 MongoDB
                  </Typography>
                  <Typography variant="body2">
                    1. 访问 /register-v2 注册新账户
                    <br />
                    2. 使用新账户登录 MongoDB 系统
                    <br />
                    3. 原有 JSON 系统数据保留，可随时切换回去
                  </Typography>
                </Alert>
              </Grid>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    从 MongoDB 切换到 JSON
                  </Typography>
                  <Typography variant="body2">
                    1. 点击页面上的"返回旧系统"链接
                    <br />
                    2. 或直接访问 /login 路由
                    <br />
                    3. 两个系统的登录状态独立，互不影响
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 管理后台入口 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              🔧 管理后台入口
            </Typography>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/admin/mongo-users"
                  startIcon={<StorageIcon />}
                >
                  MongoDB 用户管理
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/admin/users"
                  startIcon={<JsonIcon />}
                >
                  JSON 用户管理
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/admin"
                  startIcon={<StarIcon />}
                >
                  管理控制台
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SystemStatus;
